import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, User } from "lucide-react";
import { breadcrumbSchema, usePageSeo } from "./seo";
const LOGO_URL = "/logo_suanuvem.avif";
const blogPosts = [
  {
    id: 1,
    title: "Migração para Cloud: Como Reduzir Riscos, Custos e Downtime",
    excerpt: "Veja como planejar uma migração para nuvem com inventário, arquitetura alvo, estratégia de cutover, segurança, governança de custos e validação pós-migração.",
    author: "Equipe Sua Nuvem",
    date: "15 de Fevereiro de 2026",
    category: "Migração Cloud",
    readTime: "8 min"
  },
  {
    id: 2,
    title: "DevOps as a Service: Como Acelerar Entregas com CI/CD e IaC",
    excerpt: "Entenda como pipelines, infraestrutura como código, GitOps, containers e observabilidade reduzem falhas em produção e tornam releases mais previsíveis.",
    author: "Equipe Sua Nuvem",
    date: "08 de Fevereiro de 2026",
    category: "DevOps",
    readTime: "9 min"
  },
  {
    id: 3,
    title: "FinOps: Como Reduzir Custos em Nuvem com Governança",
    excerpt: "Aprenda como tagging, rightsizing, budgets, alertas, savings plans e rotinas de otimização ajudam empresas a controlar gastos em AWS, Azure, GCP e ambientes híbridos.",
    author: "Equipe Sua Nuvem",
    date: "01 de Fevereiro de 2026",
    category: "FinOps",
    readTime: "8 min"
  },
  {
    id: 4,
    title: "DevSecOps na Prática: Segurança Integrada ao Ciclo de Entrega",
    excerpt: "Veja como integrar SAST, DAST, SCA, hardening, IAM, proteção de containers e resposta a incidentes sem travar o fluxo de desenvolvimento.",
    author: "Equipe Sua Nuvem",
    date: "25 de Janeiro de 2026",
    category: "Segurança",
    readTime: "9 min"
  },
  {
    id: 5,
    title: "Observabilidade vs Monitoramento: Como Reduzir MTTR",
    excerpt: "Entenda quando métricas, logs e traces deixam de ser apenas monitoramento e passam a apoiar diagnóstico rápido, SLOs, alertas úteis e resposta a incidentes.",
    author: "Equipe Sua Nuvem",
    date: "18 de Janeiro de 2026",
    category: "Observabilidade",
    readTime: "8 min"
  },
  {
    id: 6,
    title: "Disaster Recovery: Como Definir RTO, RPO e Plano de Recuperação",
    excerpt: "Aprenda a estruturar backup, retenção, replicação, testes de restauração e runbooks para manter serviços críticos disponíveis em cenários de falha.",
    author: "Equipe Sua Nuvem",
    date: "11 de Janeiro de 2026",
    category: "Backup & DR",
    readTime: "9 min"
  }
];

function BlogPage() {
  usePageSeo({
    title: "Blog Sua Nuvem | DevOps, Cloud, FinOps e Segurança",
    description:
      "Conteúdos técnicos sobre migração cloud, DevOps, FinOps, segurança na nuvem, observabilidade e continuidade de negócios.",
    path: "/blog",
    structuredData: [
      breadcrumbSchema([
        { name: "Início", path: "/" },
        { name: "Blog", path: "/blog" },
      ]),
    ],
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32 px-6 md:px-12" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-brand-sage hover:underline mb-8"
            data-testid="back-to-home"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Voltar para Início
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight mb-6">
              Blog Sua Nuvem
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Guias práticos sobre cloud privada, DevOps, FinOps, segurança, observabilidade e continuidade de negócios para decisões técnicas mais seguras.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Link key={post.id} to={`/blog/${post.id}`}>
                <motion.article
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="border border-white/10 bg-brand-surface/30 rounded-xl overflow-hidden hover:border-brand-sage/50 transition-all duration-300 group cursor-pointer h-full"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="p-6">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-brand-sage/10 border border-brand-sage/30 rounded-full text-xs font-semibold text-brand-sage uppercase tracking-wider">
                        {post.category}
                      </span>
                    </div>

                    <h2 className="text-xl font-bold mb-3 group-hover:text-brand-sage transition-colors">
                      {post.title}
                    </h2>

                    <p className="text-gray-400 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{post.date}</span>
                      </div>
                    </div>

                    <div className="text-brand-sage text-sm font-semibold flex items-center gap-2">
                      Leitura: {post.readTime}
                    </div>
                  </div>
                </motion.article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-brand-surface/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Quer receber nossos conteúdos?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Entre em contato para saber mais sobre nossas soluções e receber insights exclusivos.
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                window.location.replace('/#contato');
              }}
              className="inline-block bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all text-lg cursor-pointer"
              data-testid="blog-cta-button"
            >
              Fale com Nossos Especialistas
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default BlogPage;
export { blogPosts };
